import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';
import { api } from '../../api/client';
import { errorMessageKey } from '../../api/api-error';
import { ensureOk, type Profile } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Notice } from '../../components/ui/notice';
import { TextField } from '../../components/ui/text-field';
import { useI18n } from '../../i18n/i18n';
import { newPasswordSchema } from '../auth/password-schema';

const schema = z.object({
  currentPassword: z.string().min(1, 'validation.required'),
  newPassword: newPasswordSchema,
});
type Values = z.infer<typeof schema>;

export function PasswordSection({ profile }: { profile: Profile }) {
  const { t } = useI18n();
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '' },
  });
  const change = useMutation({
    mutationFn: async (body: Values) => ensureOk(await api.POST('/api/v1/me/password', { body })),
    onSuccess: () => form.reset(),
  });

  return (
    <Card title={t('profile.password.title')}>
      {profile.hasPassword ? (
        <form
          className="space-y-3"
          onSubmit={form.handleSubmit((values) => change.mutate(values))}
          noValidate
        >
          <TextField
            control={form.control}
            name="currentPassword"
            type="password"
            label={t('auth.field.currentPassword')}
            autoComplete="current-password"
          />
          <TextField
            control={form.control}
            name="newPassword"
            type="password"
            label={t('auth.field.newPassword')}
            autoComplete="new-password"
            description={t('auth.field.passwordHint')}
          />
          {change.isSuccess && <Notice tone="success">{t('profile.password.changed')}</Notice>}
          {change.isError && <Notice tone="danger">{t(errorMessageKey(change.error))}</Notice>}
          <Button type="submit" variant="secondary" disabled={change.isPending}>
            {t('profile.password.submit')}
          </Button>
        </form>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t('profile.password.notSet')}{' '}
          <Link to="/forgot-password" className="text-primary hover:underline">
            {t('auth.login.forgot')}
          </Link>
        </p>
      )}
    </Card>
  );
}
